import {createSelector} from 'reselect';
import {currencyFormatter} from '@homeaway/hacommons-i18n';

import {isInBucket} from '../utils/abacusHelper';
import {
    getTranslations,
    getListingPage
} from './store';
import {
    getIsMobile,
    getLocale
} from './system';
import {getHostType, getListingIds} from './listing';

function isString(el) {
    return (typeof el === 'string');
}

function findPriceItems(listingQuote, type, subType) {
    let priceItems = [];

    if (listingQuote && listingQuote.items) {
        const listingQuoteItems = listingQuote.items.filter((item) => {
            if (isString(type) && !isString(subType)) {
                return item.type === type;
            } else if (isString(subType) && !isString(type)) {
                return item.subType === subType;
            } else if (isString(type) && isString(subType)) {
                return item.type === type && item.subType === subType;
            }
            return false;
        });

        for (let quoteItem of listingQuoteItems) {
            let priceItem = {
                label: quoteItem.label,
                amount: quoteItem.amount.localized
            };

            priceItems.push(priceItem);
        }
    }

    return priceItems;
}

const getPricingInfo = createSelector(
    [getListingIds, getListingPage],
    (listingIds, listingPage) => {
        const pricingInfo = listingPage.pricingInfo || {};
        const listingObj = pricingInfo.get ? pricingInfo.get(listingIds.listingId) : null;

        return listingObj ? listingObj.toJS() : {};
    }
);

const getListingQuote = createSelector(
    [getPricingInfo],
    (pricingInfo) => {
        const listingQuote = pricingInfo.pdpQuote ? pricingInfo.pdpQuote : pricingInfo;

        return listingQuote;
    }
);

// This is the priceDetails from the graphql-traveler-checkout partials.
// This note is important until we remove all the PDS related details.
const getPriceDetails = createSelector(
    [getListingPage],
    (listingPage) => {
        // We store the object returned from graphql into priceDetails.
        // It defaults to a POO, which doesn't have toJS.
        // This object contains errors, priceDetails and endTime.
        const priceDetails = listingPage.priceDetails && listingPage.priceDetails.toJS ? listingPage.priceDetails.toJS() : listingPage.priceDetails || {};
        // Return just the priceDetails data.
        // This is used to determine avg nightly, total, and is the chunk of
        // data passed directly to the PriceDetails component.
        return priceDetails.priceDetails || {};
    }
);

const getAverageNightlyAmountByType = (priceDetails = {averageNightlies: []}, type) => {
    return (priceDetails.averageNightlies.find((currentTotal) => currentTotal.type === type) || {perNightCost: {localized: ''}}).perNightCost.localized;
};

const getGraphQlAverageNightly = createSelector(
    [getPriceDetails],
    (priceDetails) => { // eslint-disable-line complexity
        let averageNightlyBase = '';
        let averageNightlyTotalRent = '';
        let averageNightlyTotalTrip = '';

        if (priceDetails.averageNightlies) {
            averageNightlyBase = getAverageNightlyAmountByType(priceDetails, 'BASE_RENT');
            averageNightlyTotalRent = getAverageNightlyAmountByType(priceDetails, 'TOTAL_RENT');
            averageNightlyTotalTrip = getAverageNightlyAmountByType(priceDetails, 'TOTAL_TRIP');
        }

        return {
            averageNightlyBase,
            averageNightlyTotalRent,
            averageNightlyTotalTrip
        };
    }
);

const getPdsAverageNightly = createSelector(
    [getListingQuote],
    (listingQuote) => { // eslint-disable-line complexity
        let averageNightlyBase = '';
        let averageNightlyTotalRent = '';
        let averageNightlyTotalTrip = '';

        if (listingQuote && listingQuote.averageNightly) {
            // the nightly cost of the rent
            const perNightCostBase = listingQuote.averageNightly.find((rate) => {
                return rate.type === 'BASE_RENT';
            });
            // rent + additional guest fee - discounts
            const perNightCostTotalRent = listingQuote.averageNightly.find((rate) => {
                return rate.type === 'TOTAL_RENT';
            });
            // for EU brands: rent + taxes + fees + deposits (except refundable damage deposit)
            const perNightCostTotalTrip = listingQuote.averageNightly.find((rate) => {
                return rate.type === 'TOTAL_TRIP';
            });
            averageNightlyBase = (perNightCostBase.perNightCost.amount > 0) ? perNightCostBase.perNightCost.localized : '';
            averageNightlyTotalRent = (perNightCostTotalRent.perNightCost.amount > 0) ? perNightCostTotalRent.perNightCost.localized : '';
            averageNightlyTotalTrip = (perNightCostTotalTrip.perNightCost.amount > 0) ? perNightCostTotalTrip.perNightCost.localized : '';
        }

        return {averageNightlyBase, averageNightlyTotalRent, averageNightlyTotalTrip};
    }
);

const getAverageNightly = createSelector(
    [getPdsAverageNightly, getGraphQlAverageNightly],
    (pdsAverageNightly, graphqlAverageNightly) => { // eslint-disable-line complexity
        const useGraphQl = isInBucket('ha_pdp_usePricingGraphQL');
        return useGraphQl ? graphqlAverageNightly : pdsAverageNightly;
    }
);

const getGraphQlTotals = createSelector(
    [getPriceDetails],
    (priceDetails) => {
        // Keeping this structure the same as what PDS returns.
        // 'The total will always be the first one in the array.' - Brian Griffey.
        // 'Yes, it will.' - Patrick McCarthy.
        //
        // NOTE - When total is incorrect, look here first!
        return {
            totalWithoutDeposits: priceDetails.totals && priceDetails.totals[0] ? priceDetails.totals[0].amount : '',
            totalWithDeposits: ''
        };
    }
);

const getPdsTotals = createSelector(
    [getListingQuote],
    (listingQuote) => {
        let totals = {};

        if (listingQuote && listingQuote.quoteTotals) {
            const totalWithStayCollectedFeesWithoutDepositsItem = listingQuote.quoteTotals.additionalTotals.find((additionalTotal) => {
                return additionalTotal.name === 'totalWithStayCollectedFeesWithoutDeposits';
            });
            const totalWithoutDepositsItem = listingQuote.quoteTotals.additionalTotals.find((additionalTotal) => {
                return additionalTotal.name === 'totalWithoutDeposits';
            });
            const totalWithoutDeposits = totalWithStayCollectedFeesWithoutDepositsItem || totalWithoutDepositsItem;

            const totalWithStayCollectedFeesAndDeposits = listingQuote.quoteTotals.additionalTotals.find((additionalTotal) => {
                return additionalTotal.name === 'totalWithStayCollectedFees';
            });
            const totalWithDeposits = totalWithStayCollectedFeesAndDeposits || listingQuote.quoteTotals;
            totals.totalWithoutDeposits = totalWithoutDeposits && totalWithoutDeposits.total.localized;
            totals.totalWithDeposits = totalWithDeposits && totalWithDeposits.total.localized;
        }

        return totals;
    }
);

const getTotals = createSelector(
    [getPdsTotals, getGraphQlTotals],
    (pdsTotals, graphqlTotals) => { // eslint-disable-line complexity
        const useGraphQl = isInBucket('ha_pdp_usePricingGraphQL');
        return useGraphQl ? graphqlTotals : pdsTotals;
    }
);

const getRent = createSelector(
    [getListingQuote],
    (listingQuote) => {
        let label = '';
        let amount = '';

        if (listingQuote && listingQuote.items) {
            const rentItem = listingQuote.items.find((item) => {
                return item.subType === 'RENT';
            });
            label = rentItem.label || '';
            amount = (rentItem.amount.amount > 0) ? rentItem.amount.localized : '';
        }

        return {label, amount};
    }
);

const getCustomFees = createSelector(
    [getListingQuote],
    (listingQuote) => {
        let labelAndFeeList = [];

        if (listingQuote && listingQuote.items) {
            const customFeeItems = listingQuote.items.filter((item) => {
                return item.type === 'FEE' && !item.subType;
            });
            labelAndFeeList = customFeeItems.map((fee) => {
                return {label: fee.label, amount: fee.amount.localized, raw: fee.amount.amount, currency: fee.amount.currency};
            });
        }

        return labelAndFeeList;
    }
);

const currenciesMatch = (customFees) => {
    const baseCurrency = (customFees[0] || {}).currency;

    for (let fee of customFees) {
        if (fee.currency !== baseCurrency) {
            return false;
        }
    }

    return true;
};

const consolidateFees = (customFees, currency, locale) => {
    let total = 0;

    for (let fee of customFees) {
        total += fee.raw;
    }

    return currencyFormatter.formatNumber({value: total, currencyCode: currency, locale, minimumFractionDigits: 2});
};

const getCustomFeesConsolidated = createSelector(
    [getCustomFees, getHostType, getLocale, getTranslations],
    (customFees, hostType, locale, translations) => {
        const hasMultipleFees = customFees.length >= 2;
        const hasConsistentCurrency = currenciesMatch(customFees);
        const label = hostType.owner ? translations.ownerFees : translations.managerFees;
        const currency = (customFees[0] || {}).currency;
        const consolidatedFees = consolidateFees(customFees, currency, locale);

        return hasConsistentCurrency && hasMultipleFees ? {label, amount: consolidatedFees} : null;
    }
);

const getMetaInfo = createSelector(
    [getListingQuote],
    (listingQuote) => {
        return listingQuote && listingQuote.metaInfo ? listingQuote.metaInfo : null;
    }
);

const getConditionalInstantBook = createSelector(
    [getMetaInfo, getPriceDetails],
    (metaInfo, priceDetails) => {
        const pdsPriceDetailsInstantBooking = metaInfo && metaInfo.instantBooking ? metaInfo.instantBooking : false;
        const graphqlPriceDetailsInstantBooking = !!priceDetails.instantBooking;
        return pdsPriceDetailsInstantBooking || graphqlPriceDetailsInstantBooking;
    }
);

const getStayCollectedFees = createSelector(
    [getListingQuote],
    (listingQuote) => {
        let labelAndFeeList = [];

        if (listingQuote && listingQuote.stayCollectedItems) {
            const stayCollectedItems = listingQuote.stayCollectedItems.calculableFees;
            labelAndFeeList = stayCollectedItems && stayCollectedItems.map((fee) => {
                const amount = fee.amount && fee.amount.estimated || fee.amount;
                return {label: fee.label, amount: amount.localized, raw: amount.amount, currency: amount.currency};
            });
        }

        return labelAndFeeList && labelAndFeeList.length ? labelAndFeeList : null;
    }
);

const getStayCollectedFeesDisclaimer = createSelector(
    [getListingQuote, getMetaInfo],
    (listingQuote, metaInfo) => {
        return metaInfo && metaInfo.stayFees && metaInfo.stayFees.length && metaInfo.stayFees[0] && metaInfo.stayFees[0].name ? metaInfo.stayFees[0].name.msg : null;
    }
);

const getStayCollectedFeesRefundableTotal = createSelector(
    [getListingQuote, getTranslations],
    (listingQuote, translations) => {
        const stayCollectedItems = listingQuote && listingQuote.stayCollectedItems;
        const refundableTotal = stayCollectedItems && stayCollectedItems.refundableTotal;
        const estimatedConvertedAmount = (refundableTotal && refundableTotal.estimated) ? refundableTotal.estimated : refundableTotal;

        return refundableTotal && estimatedConvertedAmount && {
            label: translations.refundableDamageDeposit,
            amount: estimatedConvertedAmount && estimatedConvertedAmount.localized,
            raw: estimatedConvertedAmount && estimatedConvertedAmount.amount,
            currency: estimatedConvertedAmount && estimatedConvertedAmount.currency
        };
    }
);

const getStayCollectedFeesRefundable = createSelector(
    [getListingQuote, getStayCollectedFeesRefundableTotal],
    (listingQuote, stayCollectedFeesRefundableTotal) => {
        if (!stayCollectedFeesRefundableTotal) {
            return null;
        }
        let labelAndFeeList = [];

        if (listingQuote && listingQuote.stayCollectedItems && listingQuote.stayCollectedItems.calculableFees) {
            const stayCollectedItems = listingQuote.stayCollectedItems.calculableFees.filter((fee) => {
                return fee.refundable;
            });
            labelAndFeeList = stayCollectedItems && stayCollectedItems.map((fee) => {
                const amount = fee.amount && fee.amount.estimated || fee.amount;
                return {label: fee.label, amount: amount.localized, raw: amount.amount, currency: amount.currency};
            });
        }

        return labelAndFeeList && labelAndFeeList.length ? labelAndFeeList : null;
    }
);

const getStayCollectedFeesNonRefundableTotal = createSelector(
    [getListingQuote, getTranslations],
    (listingQuote, translations) => {
        const stayCollectedItems = listingQuote && listingQuote.stayCollectedItems;
        const nonRefundableTotal = stayCollectedItems && stayCollectedItems.nonRefundableTotal;
        const estimatedConvertedAmount = (nonRefundableTotal && nonRefundableTotal.estimated) ? nonRefundableTotal.estimated : nonRefundableTotal;

        return nonRefundableTotal && estimatedConvertedAmount &&
            {
                label: translations.onSiteFees,
                amount: estimatedConvertedAmount && estimatedConvertedAmount.localized,
                raw: estimatedConvertedAmount && estimatedConvertedAmount.amount,
                currency: estimatedConvertedAmount && estimatedConvertedAmount.currency
            };
    }
);

const getStayCollectedFeesNonRefundable = createSelector(
    [getListingQuote, getStayCollectedFeesNonRefundableTotal],
    (listingQuote, stayCollectedFeesNonRefundableTotal) => {
        if (!stayCollectedFeesNonRefundableTotal) {
            return null;
        }

        let labelAndFeeList = [];

        if (listingQuote && listingQuote.stayCollectedItems && listingQuote.stayCollectedItems.calculableFees) {
            const stayCollectedItems = listingQuote.stayCollectedItems.calculableFees.filter((fee) => {
                return !fee.refundable;
            });
            labelAndFeeList = stayCollectedItems && stayCollectedItems.map((fee) => {
                const amount = fee.amount && fee.amount.estimated || fee.amount;
                return {label: fee.label, amount: amount.localized, raw: amount.amount, currency: amount.currency};
            });
        }

        return labelAndFeeList && labelAndFeeList.length ? labelAndFeeList : null;
    }
);

const getCustomDiscounts = createSelector(
    [getListingQuote],
    (listingQuote) => {
        let labelAndDiscountList = [];

        if (listingQuote && listingQuote.items) {
            const customDiscountItems = listingQuote.items.filter((item) => {
                return item.type === 'DISCOUNT' && !item.subType;
            });
            labelAndDiscountList = customDiscountItems.map((discount) => {
                return {label: discount.label, amount: discount.amount.localized};
            });
        }

        return labelAndDiscountList;
    }
);

const getTaxes = createSelector(
    [getListingQuote],
    (listingQuote) => {
        return findPriceItems(listingQuote, 'TAX', null);
    }
);

const getServiceFees = createSelector(
    [getListingQuote],
    (listingQuote) => {
        return findPriceItems(listingQuote, null, 'SERVICE_FEE')[0] || null;
    }
);

const getRefundableDamageDeposit = createSelector(
    [getListingQuote],
    (listingQuote) => {
        return findPriceItems(listingQuote, null, 'REFUNDABLE_DAMAGE_DEPOSIT')[0] || null;
    }
);

const getPetFee = createSelector(
    [getListingQuote],
    (listingQuote) => {
        return findPriceItems(listingQuote, null, 'PET_FEE')[0] || null;
    }
);

const getAdditionalGuestFee = createSelector(
    [getListingQuote],
    (listingQuote) => {
        return findPriceItems(listingQuote, null, 'ADDITIONAL_GUEST_FEE')[0] || null;
    }
);

const getPaymentSchedule = createSelector(
    [getListingQuote, getStayCollectedFees],
    (listingQuote, stayCollectedFees) => { // eslint-disable-line complexity
        let paymentSchedule = {};

        if (listingQuote && listingQuote.paymentPlans) {
            let paymentPlan = listingQuote.paymentPlans[0];
            let dueNow = paymentPlan.dueNow;
            let additionalPayments = paymentPlan.additionalPaymentRequests || [];

            const offPlatformExclusivePayment = additionalPayments.find((payment) => {
                return payment.supportedPaymentTypes.length === 1 && payment.supportedPaymentTypes.some((type) => {
                    return type === 'OFF_PLATFORM';
                });
            });

            if (offPlatformExclusivePayment) {
                if (stayCollectedFees && stayCollectedFees.length && listingQuote.quoteTotals && listingQuote.quoteTotals.total) {
                    paymentSchedule.dueNow = {
                        label: listingQuote.quoteTotals.total.label,
                        amount: listingQuote.quoteTotals.total.localized
                    };
                }
            } else {
                if (dueNow) {
                    paymentSchedule.dueNow = {
                        label: dueNow.label,
                        amount: dueNow.amount.localized
                    };
                }

                if (additionalPayments && !!additionalPayments.length) {
                    paymentSchedule.additionalPayments = additionalPayments.map((payment) => {
                        return {label: payment.label, amount: payment.amount.localized};
                    });
                }
            }
        }

        return paymentSchedule;
    }
);

const getPriceLabels = createSelector(
    [getTranslations, getRent, getAverageNightly, getPaymentSchedule],
    (trans, rent, averageNightly, paymentSchedule) => {
        let nights = '';
        let averageNightlyRate = '';

        if (rent && averageNightly) {
            nights = rent.label || '';
            averageNightlyRate = averageNightly.averageNightlyBase || '';
        }
        const payTheRest = paymentSchedule && paymentSchedule.additionalPayments ? trans.payTheRestLater : trans.payTheRestAtProperty;

        return {
            averageNightly: `${averageNightlyRate} × ${nights}`,
            feesPaidAtProperty: trans.feesPaidAtProperty,
            paymentSchedule: trans.paymentSchedule,
            payTheRest,
            serviceFeePopover: trans.serviceFeePopover,
            taxesAndFees: trans.taxesAndFees,
            total: trans.total,
            totalPlusRdd: trans.totalPlusRdd,
            viewDetails: trans.viewDetails
        };
    }
);

const getPriceDetailsData = createSelector(
    [
        getPriceLabels,
        getRent,
        getCustomFees,
        getCustomFeesConsolidated,
        getStayCollectedFees,
        getStayCollectedFeesDisclaimer,
        getStayCollectedFeesRefundable,
        getStayCollectedFeesNonRefundable,
        getStayCollectedFeesNonRefundableTotal,
        getStayCollectedFeesRefundableTotal,
        getCustomDiscounts,
        getTaxes,
        getServiceFees,
        getTotals,
        getRefundableDamageDeposit,
        getAdditionalGuestFee,
        getPetFee,
        getPaymentSchedule,
        getConditionalInstantBook,
        getIsMobile
    ],
    (
        labels,
        rent,
        customFees,
        customFeesConsolidated,
        stayCollectedFees,
        stayCollectedFeesDisclaimer,
        stayCollectedFeesRefundable,
        stayCollectedFeesNonRefundable,
        stayCollectedFeesNonRefundableTotal,
        stayCollectedFeesRefundableTotal,
        customDiscounts,
        tax,
        serviceFees,
        totals,
        refundableDamageDeposit,
        additionalGuestFee,
        petFee,
        paymentSchedule,
        conditionalInstantBook,
        isMobile
    ) => {
        return {
            labels,
            rentalDetails: rent,
            customFees,
            customFeesConsolidated,
            customDiscounts,
            stayCollectedFees,
            stayCollectedFeesDisclaimer,
            stayCollectedFeesRefundable,
            stayCollectedFeesNonRefundable,
            stayCollectedFeesNonRefundableTotal,
            stayCollectedFeesRefundableTotal,
            tax,
            tooltipTrigger: isMobile ? 'click' : 'hover',
            serviceFees,
            showViewDetailsLink: !!(totals || {}).totalWithoutDeposits,
            totals,
            refundableDamageDeposit,
            additionalGuestFee,
            petFee,
            paymentSchedule,
            conditionalInstantBook
        };
    }
);

export default getPriceDetailsData;
export {
    findPriceItems,
    getAdditionalGuestFee,
    getPdsAverageNightly,
    getGraphQlAverageNightly,
    getAverageNightly,
    getConditionalInstantBook,
    getCustomDiscounts,
    getCustomFees,
    getCustomFeesConsolidated,
    getMetaInfo,
    getPaymentSchedule,
    getPriceDetails,
    getPriceDetailsData,
    getPriceLabels,
    getPricingInfo,
    getListingQuote,
    getRefundableDamageDeposit,
    getRent,
    getServiceFees,
    getStayCollectedFees,
    getStayCollectedFeesRefundable,
    getStayCollectedFeesNonRefundable,
    getStayCollectedFeesNonRefundableTotal,
    getStayCollectedFeesRefundableTotal,
    getTaxes,
    getGraphQlTotals,
    getPdsTotals,
    getTotals
};
